'use client';

import { Card } from '@/components/ui/card';
import Image from 'next/image';
import { Label } from '@/components/ui/label';
import { MOCKED_IMAGES } from '@/lib/constants/mock-data';
import ImageActions from '@/components/shared/ImageActions';
import { useState } from 'react';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogTitle,
  DialogHeader,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Heart,
  Download,
  Sparkles,
  Share2,
  Scaling,
  Proportions,
  Gauge,
  CalendarDays,
  Terminal,
  Sparkle,
  Copy,
} from 'lucide-react';
import { useRecentPrompts } from '@/lib/hooks/queries/usePrompts';
import { usePromptStore } from '@/lib/store/promptStore';

// Improved bento patterns that tile efficiently (uses exactly 12 grid spaces every 8 items)
const BENTO_PATTERNS = [
  { cols: 2, rows: 2, size: 'large' }, // Large square (4 spaces)
  { cols: 1, rows: 1, size: 'small' }, // Small (1 space)
  { cols: 1, rows: 1, size: 'small' }, // Small (1 space)
  { cols: 2, rows: 1, size: 'wide' }, // Wide (2 spaces) = 8 spaces total for row
  { cols: 1, rows: 1, size: 'small' }, // Small (1 space)
  { cols: 1, rows: 1, size: 'small' }, // Small (1 space)
  { cols: 1, rows: 1, size: 'small' }, // Small (1 space)
  { cols: 1, rows: 1, size: 'small' }, // Small (1 space) = 4 spaces total for row
];

function FeedItem({
  image,
  onMouseDown,
  pattern,
}: {
  image: (typeof MOCKED_IMAGES)[number];
  onMouseDown: () => void;
  pattern: (typeof BENTO_PATTERNS)[number];
}) {
  const sizeClasses: Record<string, string> = {
    large: 'col-span-2 row-span-2',
    wide: 'col-span-2 row-span-1',
    tall: 'col-span-1 row-span-2',
    small: 'col-span-1 row-span-1',
  };

  return (
    <DialogTrigger asChild>
      <div
        className={`group rounded-xl overflow-hidden bg-stone-900 relative cursor-pointer ${
          sizeClasses[pattern.size]
        }`}
        onMouseDown={onMouseDown}
      >
        <Image
          src={image.src}
          alt={image.title}
          fill
          className='w-full h-full object-cover transition-transform duration-300 group-hover:scale-105'
        />
        <div className='absolute top-3 right-3 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-10'>
          <ImageActions />
        </div>
      </div>
    </DialogTrigger>
  );
}

function FeedDialogContent({
  image,
}: {
  image: (typeof MOCKED_IMAGES)[number] | null;
}) {
  if (!image) return null;

  return (
    <DialogContent className='bg-[#1E1F22] border border-white/10 backdrop-blur-md rounded-lg !max-w-6xl'>
      <DialogHeader>
        <DialogTitle className='sr-only truncate'>{image.title}</DialogTitle>
      </DialogHeader>
      <div className='flex flex-col md:flex-row gap-8 w-full'>
        {/* Left: Image + buttons */}
        <div className='md:w-1/2 w-full flex flex-col items-center'>
          <div className='w-full bg-[#252527] rounded-2xl p-6 shadow-xl flex flex-col items-center'>
            <div className='w-full aspect-square relative rounded-xl overflow-hidden mb-4 bg-neutral-900'>
              <Image
                src={image.src}
                alt={image.title}
                fill
                className='object-cover w-full'
              />
            </div>
            <div className='flex w-full gap-2 mt-auto'>
              <Button
                variant='outline'
                className='flex-1 flex items-center justify-center gap-2 h-10 border bg-zinc-700/30 text-white text-sm font-semibold border-white/10 hover:bg-zinc-700/10 transition-all duration-300'
              >
                <Heart className='w-5 h-5 text-[#ef4444]' fill='#ef4444' />
                Liked
              </Button>
              <Button
                variant='outline'
                className='flex-1 flex items-center justify-center gap-2 h-10 border bg-zinc-700/30 text-white text-sm font-semibold border-white/10 hover:bg-zinc-700/10 transition-all duration-300'
              >
                <Download className='w-5 h-5' />
                Download
              </Button>
              <Button
                variant='outline'
                className='flex-1 flex items-center justify-center gap-2 h-10 border bg-zinc-700/30 text-white text-sm font-semibold border-white/10 hover:bg-zinc-700/10 transition-all duration-300'
              >
                <Sparkles className='w-5 h-5' />
                Generate Similar
              </Button>
              <Button className='flex-1 flex items-center justify-center gap-2 h-10 bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-sm font-semibold shadow border border-fuchsia-600 transition-all duration-300'>
                <Share2 className='w-5 h-5' />
                Share
              </Button>
            </div>
          </div>
        </div>
        {/* Right: Content */}
        <div className='md:w-1/2 w-full flex flex-col gap-4 items-start'>
          <Label className='self-start text-2xl font-semibold text-stone-50/90 mb-2 truncate text-wrap w-[90%] mt-4'>
            Astronaut hanging in outer space near the colourful nebula.
          </Label>
          <div>
            <Label className='text-neutral-400 text-sm mb-1 font-medium'>
              Prompt
            </Label>
            <div className='text-stone-50/90 text-lg mb-2'>
              Astronaut hanging in outer space near the colourful nebula,
              wearing a helmet and a suit, with arms stretched towards the
              nebula. the gas nebula in distance glows orange and yellow, with
              streaks of light radiating from it. the background is dark, with
              stars visible in the distance.
            </div>
            <Button className='bg-zinc-700/30 text-stone-50/90 rounded-md px-3 py-1 font-medium mb-4 border border-white/10 hover:bg-zinc-700/10 transition-all duration-300'>
              <Copy className='w-4 h-4' />
              Copy Prompt
            </Button>
          </div>
          <div className='grid grid-cols-2 gap-4 border-t border-white/10 pt-4 text-stone-50/80 text-sm'>
            <div>
              <div className='flex items-center gap-2 text-neutral-400 text-base'>
                <Scaling className='w-4 h-4' />
                <Label className='font-semibold'>Size</Label>
              </div>
              <div className='text-lg mt-1'>1024x1024</div>
            </div>
            <div>
              <div className='flex items-center gap-2 text-neutral-400 text-base'>
                <Proportions className='w-4 h-4' />
                <Label className='font-semibold'>Scale</Label>
              </div>
              <div className='text-lg mt-1'>1:1</div>
            </div>
            <div>
              <div className='flex items-center gap-2 text-neutral-400 text-base'>
                <Gauge className='w-4 h-4' />
                <Label className='font-semibold'>Speed</Label>
              </div>
              <div className='text-lg mt-1'>Ultra</div>
            </div>
            <div>
              <div className='flex items-center gap-2 text-neutral-400 text-base'>
                <Sparkle className='w-4 h-4' />
                <Label className='font-semibold'>AI Model</Label>
              </div>
              <div className='text-lg mt-1'>Minimax</div>
            </div>
            <div>
              <div className='flex items-center gap-2 text-neutral-400 text-base'>
                <Terminal className='w-4 h-4' />
                <Label className='font-semibold'>Usecase</Label>
              </div>
              <div className='text-lg mt-1'>Freeform</div>
            </div>
            <div>
              <div className='flex items-center gap-2 text-neutral-400 text-base'>
                <CalendarDays className='w-4 h-4' />
                <Label className='font-semibold'>Created</Label>
              </div>
              <div className='text-lg mt-1'>20 December, 2024 11:22 PM</div>
            </div>
          </div>
        </div>
      </div>
    </DialogContent>
  );
}

export default function Feed() {
  const [selected, setSelected] = useState<string | null>(null);

  const { latestPrompt } = usePromptStore();

  if (!latestPrompt || latestPrompt.output_images_urls.length === 0) {
    return <EmptyState />;
  }

  // Flatten all image URLs from all prompts, keeping track of their prompt for possible future use
  const images = latestPrompt.output_images_urls.map((url) => ({
    url,
    prompt: latestPrompt.prompt,
    created_at: latestPrompt.created_at,
  }));

  console.log(images);

  return (
    <Dialog>
      <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 auto-rows-[180px] md:auto-rows-[220px] lg:auto-rows-[240px] xl:auto-rows-[280px] gap-3 md:gap-4 lg:gap-6 p-4 w-full max-w-3xl md:max-w-5xl lg:max-w-6xl xl:max-w-7xl mx-auto grid-flow-dense'>
        {images.map((img, idx) => {
          const pattern = BENTO_PATTERNS[idx % BENTO_PATTERNS.length];
          return (
            <FeedItem
              key={img.url}
              image={{
                src: img.url,
                title: img.prompt,
                createdAt: Date.parse(img.created_at || '') || Date.now(),
              }}
              pattern={pattern}
              onMouseDown={() => setSelected(img.url)}
            />
          );
        })}
      </div>

      {/* For now, FeedDialogContent expects an object like MOCKED_IMAGES, so we find the selected image info */}
      <FeedDialogContent
        image={
          selected ? { src: selected, title: '', createdAt: Date.now() } : null
        }
      />
    </Dialog>
  );
}

function EmptyState() {
  return (
    <div className='flex h-full w-full items-center justify-center'>
      <Card className='flex flex-col items-center justify-center w-full max-w-md py-16 border-none shadow-none gap-4'>
        <Image
          src='/image-creator.svg'
          alt='Created images'
          width={64}
          height={64}
          className='opacity-80'
        />
        <Label className='text-stone-50 text-xl font-semibold text-center'>
          Created images will appear here.
        </Label>
        <Label className='text-stone-50/60 text-base max-w-md mx-auto font-normal text-center'>
          Looks like you haven&apos;t created anything yet!
          <br />
          Describe what you want then upload an image after which click
          &apos;Create&apos;.
        </Label>
      </Card>
    </div>
  );
}
